import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type {
  State,
  PublicHoliday,
  AllHolidayOutput,
  MergedHoliday,
  AllMergedOutput,
} from "../types";

interface HolidayContextType {
  holidays: MergedHoliday[];
  allHolidays: MergedHoliday[];
  loading: boolean;
  error: string | null;
  currentYear: number;
  currentMonth: number;
  // selectedStates: State[];
  setCurrentMonth: (month: number) => void;
  loadYear: (year: number) => Promise<void>;
  ensureYearsLoaded: (years: number[]) => Promise<void>;
}

const HolidayContext = createContext<HolidayContextType | undefined>(undefined);

export const useHolidays = () => {
  const context = useContext(HolidayContext);
  if (!context) {
    throw new Error("useHolidays must be used within HolidayProvider");
  }
  return context;
};

interface HolidayProviderProps {
  children: ReactNode;
}

export const HolidayProvider: React.FC<HolidayProviderProps> = ({ children }) => {
  const [holidayData, setHolidayData] = useState<AllMergedOutput>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1); // 1-12

  const loadYear = async (year: number) => {
    if (holidayData[year]) {
      setCurrentYear(year);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/data/${year}.json`);
      if (!response.ok) {
        throw new Error(`No data available for ${year}`);
      }
      const data: MergedHoliday[] = await response.json();
      setHolidayData((prev) => ({ ...prev, [year]: data }));
      setCurrentYear(year);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load holiday data");
      setCurrentYear(year);
      console.error("Error loading holiday data:", err);
    } finally {
      setLoading(false);
    }
  };

  const ensureYearsLoaded = async (years: number[]) => {
    const yearsToLoad = years.filter(year => !holidayData[year]);
    if (yearsToLoad.length === 0) return;

    await Promise.all(yearsToLoad.map(async (year) => {
      try {
        const response = await fetch(`/data/${year}.json`);
        if (response.ok) {
          const data: MergedHoliday[] = await response.json();
          setHolidayData((prev) => ({ ...prev, [year]: data }));
        }
      } catch (err) {
        console.error(`Error loading holiday data for ${year}:`, err);
      }
    }));
  };

  // Load current year on mount and ensure next year is loaded for countdown
  useEffect(() => {
    const initLoad = async () => {
      await loadYear(currentYear);
      // Load next year in background for countdown (don't wait)
      ensureYearsLoaded([currentYear + 1]);
    };
    initLoad();
  }, []);

  // Get all holidays from loaded years (for countdown)
  const allHolidays = React.useMemo(() => {
    return Object.values(holidayData).flat();
  }, [holidayData]);

  // Filter holidays based on selected states and current month
  const holidays = React.useMemo(() => {
    const yearHolidays = holidayData[currentYear] || [];

    return yearHolidays.filter((holiday) => {
      // Filter by month
      const holidayMonth = parseInt(holiday.dateString.substring(4, 6));
      if (holidayMonth !== currentMonth) {
        return false;
      }

      // Show federal holidays to all states
      if (holiday.type === "federal") {
        return true;
      }

      // Show state-specific holidays only if at least one selected state matches
      return holiday.states;
    });
  }, [holidayData, currentYear, currentMonth]);

  return (
    <HolidayContext.Provider
      value={{
        holidays,
        allHolidays,
        loading,
        error,
        currentYear,
        currentMonth,
        setCurrentMonth,
        loadYear,
        ensureYearsLoaded,
      }}
    >
      {children}
    </HolidayContext.Provider>
  );
};
